import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = 'Maybank Tower, 100 Jalan Tun Perak, 50050 Kuala Lumpur, Malaysia';
  const billingAddress = 'Maybank Tower, 100 Jalan Tun Perak, 50050 Kuala Lumpur, Malaysia';
  const formatCurrency = (amount: number, currency: string) => {
    return formatCurrencyAmount(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-4687">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-4688">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4689">
          <div className="flex items-center justify-between h-16" data-id="element-4690">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-4691">
              {t('pages.ChallengeDispute.challenge_dispute')}
            </h1>
            <button onClick={handleClose} aria-label="Close" className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-4692">
              <XIcon size={20} aria-hidden="true" data-id="element-4693" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-4694">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-4695">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-4696">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-4697">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-4698">
                <span data-id="element-4699">{t('pages.ChallengeDispute.challenge_details')}</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-4700" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-4701" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-4702">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-4703">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4704" />}
                    {t('pages.ChallengeDispute.challenge_reason')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4705">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4706" />}
                    {t('pages.ChallengeDispute.supporting_evidence')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-4707">
                <span data-id="element-4708">{t('pages.ChallengeDispute.transaction_details')}</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-4709" /> : <ChevronDownIcon size={16} data-id="element-4710" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-4711">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4712">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4713" />}
                    {t('pages.ChallengeDispute.order_details')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4714">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4715" />}
                    {t('pages.ChallengeDispute.customer_details')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-4716">
                <span data-id="element-4717">{t('pages.ChallengeDispute.other_evidence')}</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-4718" /> : <ChevronDownIcon size={16} data-id="element-4719" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-4720">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4721">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4722" />}
                    {t('pages.ChallengeDispute.other_supporting_documents')}
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-4723">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-4724">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4725">
                  <div data-id="element-4726">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4727">
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4728">
                      {t('pages.ChallengeDispute.your_responses_will_help_us_recommend')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4729">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4730">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4731">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4732">
                      <div className="text-gray-500" data-id="element-4733">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4734">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4735">
                      <div className="text-gray-500" data-id="element-4736">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4737">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4738">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4739">
                  <div data-id="element-4740">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4741">
                      {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4742">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-4743">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4744">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4745" />

                        <span className="text-base text-gray-900" data-id="element-4746">
                          {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4747">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4748" />

                        <span className="text-base text-gray-900" data-id="element-4749">
                          {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4750">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4751" />

                        <span className="text-base text-gray-900" data-id="element-4752">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4753">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4754" />

                        <span className="text-base text-gray-900" data-id="element-4755">
                          {t('pages.ChallengeDispute.other_reasons')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-4756">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4757">
                      {t('pages.ChallengeDispute.what_type_of_productservice_was_the')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4758">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-4759">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4760">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4761" />

                        <div className="flex-1" data-id="element-4762">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4763">
                            {t('pages.ChallengeDispute.physical_product')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4764">
                            {t('pages.ChallengeDispute.physical_goods_to_be_delivered_to')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4765">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4766" />

                        <div className="flex-1" data-id="element-4767">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4768">
                            {t('pages.ChallengeDispute.digital_product_or_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4769">
                            {t('pages.ChallengeDispute.purchased_access_to_software_digital_files')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4770">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4771" />

                        <div className="flex-1" data-id="element-4772">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4773">
                            {t('pages.ChallengeDispute.offline_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4774">
                            {t('pages.ChallengeDispute.labour_or_assistant_services_provided_either')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4775">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4776" />

                        <div className="flex-1" data-id="element-4777">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4778">
                            {t('pages.ChallengeDispute.reservation_or_booking')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4779">
                            {t('pages.ChallengeDispute.services_related_to_reservations_or_bookings')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4780">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4781" />

                        <span className="text-base text-gray-900" data-id="element-4782">
                          {t('pages.ChallengeDispute.other')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4783">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4784">
                    {t('pages.ChallengeDispute.cancel')}
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-4785">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-4786">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4787">
                  <div data-id="element-4788">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4789">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-4790">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-4791">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4792">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4793">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4794">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4795">
                      <div className="text-gray-500" data-id="element-4796">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4797">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4798">
                      <div className="text-gray-500" data-id="element-4799">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4800">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4801">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4802">
                  <div data-id="element-4803">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4804">
                      {t('pages.ChallengeDispute.recommended_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4805">
                      {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-4806">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-4807">
                          <strong data-id="element-4808">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-4809">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4810">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4811">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-4812" />
                            {requirements.language}
                            <br data-id="element-4813" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-4814">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-4815">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-4816">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4817">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4818" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4819" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4820">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-4821">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4822">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-4823">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4824">
                              <EyeIcon size={20} data-id="element-4825" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4826">
                              <Trash2Icon size={20} data-id="element-4827" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-4828">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-4829">
                      <UploadIcon size={16} data-id="element-4830" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-4831">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-4832" />
                  </div>

                  <div data-id="element-4833">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-4834">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-4835" />

                      <span className="text-sm text-gray-900" data-id="element-4836">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4837">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4838">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-4839">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-4840">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4841">
                  <div data-id="element-4842">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4843">
                      {t('pages.ChallengeDispute.order_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4844">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-4845">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4846">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4847">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4848">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4849">
                      <div className="text-gray-500" data-id="element-4850">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4851">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4852">
                      <div className="text-gray-500" data-id="element-4853">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4854">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4855">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4856">
                  <div data-id="element-4857">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4858">
                      {t('pages.ChallengeDispute.product_details')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4859">
                      <span data-id="element-4860">
                        {t('pages.ChallengeDispute.iphone7_5')}{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4861">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4862">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4863" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4864">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4865">
                      {t('pages.ChallengeDispute.shipping_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4866">
                      <span data-id="element-4867">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4868">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4869">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4870" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4871">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4872">
                      {t('pages.ChallengeDispute.shipping_carrier')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4873">
                      <span data-id="element-4874">{t('pages.ChallengeDispute.sameday')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4875">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4876">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4877" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4878">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4879">
                      {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4880">
                      {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                      <span className="font-medium" data-id="element-4881">
                        {t('pages.ChallengeDispute.physical_product')}
                      </span>
                      {t('pages.ChallengeDispute.we_recommend_you_to_also_provide')}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-4882">
                      <li data-id="element-4883">{t('pages.ChallengeDispute.shipping_date')}</li>
                      <li data-id="element-4884">
                        {t('pages.ChallengeDispute.shipping_tracking_number')}
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4885">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4886">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-4887" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-4888" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-4889">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-4890">
                      <UploadIcon size={16} data-id="element-4891" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-4892">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-4893" />
                  </div>

                  <div data-id="element-4894">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-4895">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-4896" />

                      <span className="text-sm text-gray-900" data-id="element-4897">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4898">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4899">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-4900">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-4901">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4902">
                  <div data-id="element-4903">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4904">
                      {t('pages.ChallengeDispute.customer_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4905">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as_2')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-4906">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4907">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4908">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4909">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4910">
                      <div className="text-gray-500" data-id="element-4911">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4912">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4913">
                      <div className="text-gray-500" data-id="element-4914">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4915">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4916">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4917">
                  <div data-id="element-4918">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4919">
                      {t('pages.ChallengeDispute.customer_name')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4920">
                      <span data-id="element-4921">{t('pages.ChallengeDispute.test_name')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4922">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4923">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4924" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4925">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4926">
                      {t('pages.ChallengeDispute.customer_email')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4927">
                      <span data-id="element-4928">
                        {t('pages.ChallengeDispute.customerexamplecom')}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4929">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4930">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4931" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4932">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4933">
                      {t('pages.ChallengeDispute.customer_purchase_ip')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4934">
                      <span data-id="element-4935">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4936">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4937">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4938" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4939">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-4940">
                      {t('pages.ChallengeDispute.customer_billing_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4941">
                      <span data-id="element-4942">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-4943">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4944">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-4945" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4946">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4947">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4948">
                      {t('pages.ChallengeDispute.we_recommend_you_to_provide_the')}
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4949">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing_2')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4950">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-4951" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-4952" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-4953">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-4954">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-4955">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4956">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4957" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4958" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4959">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-4960">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4961">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-4962">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4963">
                              <EyeIcon size={20} data-id="element-4964" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4965">
                              <Trash2Icon size={20} data-id="element-4966" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-4967">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-4968">
                      <UploadIcon size={16} data-id="element-4969" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-4970">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-4971" />
                  </div>

                  <div data-id="element-4972">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-4973">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-4974" />

                      <span className="text-sm text-gray-900" data-id="element-4975">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4976">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4977">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-4978">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-4979">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4980">
                  <div data-id="element-4981">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4982">
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4983">
                      {t('pages.ChallengeDispute.if_you_have_any_other_documents')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4984">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4985">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4986">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4987">
                      <div className="text-gray-500" data-id="element-4988">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4989">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4990">
                      <div className="text-gray-500" data-id="element-4991">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4992">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4993">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4994">
                  <div data-id="element-4995">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4996">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4997">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-4998" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-4999" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5000">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5001">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5002">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5003">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5004" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5005" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5006">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5007">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5008">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5009">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5010">
                              <EyeIcon size={20} data-id="element-5011" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5012">
                              <Trash2Icon size={20} data-id="element-5013" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5014">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5015">
                      <UploadIcon size={16} data-id="element-5016" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5017">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5018" />
                  </div>

                  <div data-id="element-5019">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5020">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5021" />

                      <span className="text-sm text-gray-900" data-id="element-5022">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5023">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5024">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5025">
                    {t('pages.ChallengeDispute.review')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-5026">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5027">
                  <div data-id="element-5028">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5029">
                      {t('pages.ChallengeDispute.review_your_challenge')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5030">
                      {t('pages.ChallengeDispute.please_confirm_that_the_information_you')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5031">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5032">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5033">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5034">
                      <div className="text-gray-500" data-id="element-5035">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5036">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5037">
                      <div className="text-gray-500" data-id="element-5038">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5039">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5040">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5041">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5042">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5043">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5044">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5045">
                        <span className="text-sm text-gray-600" data-id="element-5046">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5047">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5048">
                        <span className="text-sm text-gray-600" data-id="element-5049">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5050">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-5051">
                          <span className="text-sm text-gray-600" data-id="element-5052">
                            {t('pages.ChallengeDispute.supporting_evidence')}
                          </span>
                          <div className="space-y-2" data-id="element-5053">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-5054">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5055">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5056">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5057" />

                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5058" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-5059">
                                  <div className="text-gray-900 font-medium" data-id="element-5060">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-5061">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-5062">
                                  <EyeIcon size={16} data-id="element-5063" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5064">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5065">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5066">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5067">
                        <span className="text-sm text-gray-600" data-id="element-5068">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5069">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5070">
                        <span className="text-sm text-gray-600" data-id="element-5071">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5072">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5073">
                        <span className="text-sm text-gray-600" data-id="element-5074">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5075">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5076">
                        <span className="text-sm text-gray-600" data-id="element-5077">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5078">
                          {t('pages.ChallengeDispute.customerexamplecom')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5079">
                        <span className="text-sm text-gray-600" data-id="element-5080">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5081">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5082">
                        <span className="text-sm text-gray-600" data-id="element-5083">
                          {t('pages.ChallengeDispute.customer_billing_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5084">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-5085">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-5086" />

                      <p className="text-sm text-gray-900" data-id="element-5087">
                        {t('pages.ChallengeDispute.please_attach_at_least_one_of')}
                      </p>
                    </div>}

                  <div data-id="element-5088">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5089">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5090" />

                      <span className="text-sm text-gray-900" data-id="element-5091">
                        {t('pages.ChallengeDispute.i_understand_that_the_current_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5092">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5093">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5094">
                    {t('pages.ChallengeDispute.submit')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-5095">
                <div className="text-center mb-8" data-id="element-5096">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6" data-id="element-5097">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5098">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#10B981" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" data-id="element-5099" />

                      <path d="M6 24L24 34L42 24" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5100" />

                      <path d="M6 34L24 44L42 34" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5101" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5102">
                    {t('pages.ChallengeDispute.your_dispute_challenge_has_been_submitted')}
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-5103">
                    {t('pages.ChallengeDispute.thank_you_for_submitting_your_information')}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-5104">
                  <div data-id="element-5105">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5106">
                      {t('pages.ChallengeDispute.dispute_challenge')}
                    </h2>

                    <div className="space-y-6" data-id="element-5107">
                      <div data-id="element-5108">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5109">
                          {t('pages.ChallengeDispute.challenge_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5110">
                          <div className="flex" data-id="element-5111">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5112">
                              {t('pages.ChallengeDispute.reason_for_challenge')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5113">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5114">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5115">
                              {t('pages.ChallengeDispute.type_of_product_or_service')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5116">
                              {t('pages.ChallengeDispute.physical_product')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5117">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5118">
                              {t('pages.ChallengeDispute.supporting_evidence')}
                            </span>
                            <div className="flex-1" data-id="element-5119">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-5120">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5121">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5122">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5123" />

                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5124" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-5125">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-5126">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-5127">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-5128">
                                      <EyeIcon size={18} data-id="element-5129" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-5130">
                                  {t('pages.ChallengeDispute.no_files_uploaded')}
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-5131">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5132">
                          {t('pages.ChallengeDispute.transaction_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5133">
                          <div className="flex" data-id="element-5134">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5135">
                              {t('pages.ChallengeDispute.product_details')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5136">
                              {t('pages.ChallengeDispute.iphone7_5')}{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5137">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5138">
                              {t('pages.ChallengeDispute.shipping_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5139">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5140">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5141">
                              {t('pages.ChallengeDispute.customer_name')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5142">
                              {t('pages.ChallengeDispute.test_name')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5143">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5144">
                              {t('pages.ChallengeDispute.customer_email')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5145">
                              {t('pages.ChallengeDispute.customerexamplecom')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5146">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5147">
                              {t('pages.ChallengeDispute.customer_purchase_ip')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5148">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-5149">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5150">
                              {t('pages.ChallengeDispute.customer_billing_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5151">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-5152">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-5153">
                      {t('pages.ChallengeDispute.view_all_disputes')}
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-5154">
                      {t('pages.ChallengeDispute.back_to_dispute_details')}
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5155">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5156">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-5157">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5158"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5159">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5160">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-5161">
                <div className="flex items-start gap-4" data-id="element-5162">
                  <div className="flex-shrink-0" data-id="element-5163">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5164">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-5165" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-5166">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5167">
                      {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-5168">
                      {t('pages.ChallengeDispute.submitting_a_challenge_without_supporting_evidence')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-5169">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-5170">
                  {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-5171">
                  {t('pages.ChallengeDispute.proceed_anyway')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}